import { createContext, useContext } from 'react'
import type { SignatureCard } from './signature'

/**
 * The digital business card as a MODAL, openable from anywhere in the shell.
 * David, 4 Oct.
 *
 * ── RELEASED SEPARATELY FROM THE SIGNATURE (David, 4 Oct) ───────────────────
 *
 * On 24 Sep (OPS-57) the card went behind `SIGNATURE_RELEASED = false` with the
 * signature, because Q&A 9.4 says "Not yet" to updating a signature and 9.3
 * says not to use the new logo yet. On 4 Oct David was asked exactly that —
 * a branded card people show clients is external use of the logo — and chose
 * to release the card now. So the card has its own switch and the signature
 * keeps its hold. Do not re-couple them on the strength of the 24 Sep comment
 * in Profile.tsx; it is about the signature.
 *
 * A constant, like SIGNATURE_RELEASED, for the same reason: switching it is a
 * code change anyway (the hub widget, the menu row and the dialog all read it).
 */
export const CARD_RELEASED = true

/**
 * ── NO LOGO ON THE CARD, FOR NOW (David, 11 Oct) ────────────────────────────
 *
 * The lockup on the band, the strata watermark behind it, the mark in the
 * middle of the QR and the mark on the hub teaser are ALL logo artwork, and
 * all four are off. Q&A 9.3 still says not to use the new logo yet, and a card
 * people show clients is the most external thing the hub makes. The company
 * is still named, in words, at the foot ("Conetec"). Turning it back on is
 * this constant; the artwork and CARD_LOGO_SRC are kept, not deleted.
 */
export const CARD_SHOWS_LOGO = false

/**
 * How anything inside the shell opens the card. `null` means there is no card
 * to open — outside the shell, or while CARD_RELEASED is false — and every
 * trigger renders NOTHING rather than an inert control (SHL-8's trap).
 *
 * Its own module rather than AppShell.tsx for openLanes.ts's reason: the shell
 * imports an SVG the test bundler cannot resolve, so a component that imports
 * the shell is one the suite cannot mount.
 *
 * ── THE PROVIDER IS BELOW THE ROUTE, SO CALL THIS FROM A CHILD ──────────────
 *
 * A route renders `<AppShell>` from its own body; a hook called in that body
 * sits ABOVE the provider and gets the default. That is how #/wiki's Ask One
 * button did nothing for five days (see `useAskOne`). Call it from a component
 * rendered as a child of the shell — `MyCardWidget`, `OpenCardButton`.
 */
export const BusinessCardContext = createContext<(() => void) | null>(null)

export function useBusinessCard(): (() => void) | null {
  return useContext(BusinessCardContext)
}

/**
 * A file the person will find again in Downloads a week later. Moved here from
 * Profile.tsx with the card.
 */
export function vcfFileName(card: Pick<SignatureCard, 'displayName'>): string {
  // Accents folded first: half the company writes in Spanish, and \w is ASCII,
  // so "Ana María" would otherwise come out "ana-mar-a".
  const slug = card.displayName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^\w]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase()
  return `${slug || 'contact'}-conetec.vcf`
}

/**
 * A `tel:` target. Cut at the extension marker FIRST — stripping non-digits
 * alone welds the extension's digits onto the number ("x21" becomes "21"),
 * which dials a different phone and looks correct in every review. The same
 * rule `buildSignatureHtml` applies inline.
 */
export function telHref(phone: string): string {
  return phone.split(/[a-zA-Z#]/)[0].replace(/[^\d+]/g, '')
}
